import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconCheck, IconClose, IconImages, IconWifiOff, MarkBoot } from '../components/Icon';
import {
  cancel, enqueue, estimateLabel, formatDuration, retry, useMedia,
} from '../lib/media';
import type { MediaItem } from '../lib/media';
import { useFlags } from '../lib/content';
import { useSession } from '../lib/session';
import { useOnline } from '../lib/online';
import { now } from '../lib/time';
import { TEAM_FALLBACK } from '@mv/contracts';

/**
 * Artboard 1f — the money path.
 * The queue is the whole design. A guest who doubts that their photos are going
 * up will upload them again, and now there are duplicates on a network that was
 * already the bottleneck — so every item says what it is doing, in words, with
 * a retry and a cancel that work.
 */
export default function Upload() {
  const session = useSession();
  const navigate = useNavigate();
  const media = useMedia();
  const flags = useFlags();
  const online = useOnline();
  const [caption, setCaption] = useState('');
  const libraryInput = useRef<HTMLInputElement>(null);

  const mine = media.filter((item) => item.uploaderId === session?.userId);
  const batch = mine.filter((item) => item.state !== 'done' || now() - item.createdAt < 5 * 60_000);
  const inFlight = batch.filter((item) => item.state === 'uploading' || item.state === 'queued' || item.state === 'processing');
  const doneCount = batch.filter((item) => item.state === 'done').length;
  const readOnly = flags.data?.READ_ONLY ?? false;

  async function pick(files: FileList | null) {
    if (!files || !session) return;
    // `TEAM_FALLBACK` rather than nothing: the server stamps the real team on
    // the row from the guest's own profile, and this is only the optimistic
    // local copy the grid shows until it answers. Attributing a photograph oddly
    // for a second beats not showing it.
    await enqueue(
      Array.from(files),
      { userId: session.userId, name: session.name, team: session.team ?? TEAM_FALLBACK },
      caption || undefined,
    );
  }

  if (readOnly) {
    return (
      <div className="page empty" style={{ minHeight: '70dvh', justifyContent: 'center' }}>
        <MarkBoot className="mark" size={44} />
        <h4 style={{ margin: 0 }}>Uploads are paused</h4>
        <p>{flags.data?.readOnlyMessage}</p>
        <button type="button" className="btn btn-secondary" onClick={() => navigate('/gallery')}>Back to the gallery</button>
      </div>
    );
  }

  return (
    <div className="page page-narrow page-narrow-sm" style={{ padding: '10px 20px 96px' }}>
      <h3 style={{ margin: 0 }}>Add photos</h3>

      {!online && (
        <div className="pill" style={{ marginInline: 0 }} role="status">
          <IconWifiOff size={14} /> Offline — anything you add waits here and goes up on its own.
        </div>
      )}

      {/* There were two buttons here, "Take one now" and "From my library".
          The first set `capture="environment"`, the second set `multiple`.

          CHECKED ON A REAL IPHONE, against production, because this is the one
          claim a laptop cannot settle: desktop browsers ignore `capture`
          entirely, so both buttons look identical there whatever a phone does.
          They are identical on the phone too — "Take one now" opened the same
          photo picker as "From my library", no viewfinder.

          So two buttons of different weight led to one identical sheet, and the
          louder of them was the one that could only return a single file. The
          primary action penalised the guest who trusted it, and choosing the
          other required knowing something the screen never said. One button
          now, and it is the one that takes more than one file.

          NOT settled by that check, and tracked separately: whether the sheet
          offers a camera at all. If it does not, nothing in this app reaches
          the camera — which was already true before this change, since the
          button that claimed to was not doing it. */}
      <div className="pick">
        <button
          type="button" className="btn btn-primary"
          onClick={() => libraryInput.current?.click()}
        >
          <IconImages size={26} /> Choose photos
        </button>
      </div>

      <input
        ref={libraryInput} type="file" accept="image/*,video/*" multiple
        hidden onChange={(e) => { void pick(e.target.files); e.target.value = ''; }}
      />

      <div className="field" style={{ marginTop: 16 }}>
        <label htmlFor="caption">
          {inFlight.length > 1
            ? `One caption for all ${inFlight.length} — optional, and you can edit them later`
            : 'A caption — optional, and you can edit it later'}
        </label>
        <input
          id="caption" className="input" style={{ minHeight: 48, fontSize: 15 }}
          value={caption} onChange={(e) => setCaption(e.target.value)}
          placeholder="Mehndi night, everybody dancing"
        />
      </div>

      {batch.length > 0 && (
        <>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginTop: 20 }}>
            <h5 style={{ margin: 0 }}>
              {inFlight.length > 0 ? `Uploading · ${doneCount} of ${batch.length} done` : `${doneCount} in the gallery`}
            </h5>
            {inFlight[0] && <span style={{ fontSize: 11, opacity: 0.55 }}>{estimateLabel(inFlight[0])}</span>}
          </div>
          <div className="q">
            {batch.map((item) => <QueueRow key={item.id} item={item} />)}
          </div>
        </>
      )}

      <p style={{ fontSize: 11, opacity: 0.5, marginTop: 14 }}>
        Videos up to three minutes. Anything longer is turned away before it starts uploading, not after.
      </p>

      {/* The way out used to be a ghost "Done" in the top-right corner: the
          lowest-affordance style we have, in the hardest place on a phone to
          reach, directly under the header's own row of controls. It sits at the
          foot now, in the thumb arc, as a real button that says where it goes.
          Sticky rather than fixed so it cannot cover the last queue row. */}
      <div className="upload-actions">
        <button
          type="button"
          className={`btn btn-block ${inFlight.length > 0 ? 'btn-secondary' : 'btn-primary'}`}
          style={{ minHeight: 52, fontSize: 16 }}
          onClick={() => navigate('/gallery')}
        >
          {inFlight.length > 0
            ? `Done — ${inFlight.length} still going up`
            : doneCount > 0
              ? `See ${doneCount === 1 ? 'it' : `all ${doneCount}`} in the gallery`
              : 'Back to the gallery'}
        </button>
        {inFlight.length > 0 && (
          <p className="upload-actions-note">These keep uploading if you leave this screen.</p>
        )}
      </div>
    </div>
  );
}

function QueueRow({ item }: { item: MediaItem }) {
  const cls = [
    'card', 'q-item',
    item.state === 'failed' ? 'is-failed' : '',
    item.state === 'rejected' ? 'is-rejected' : '',
    item.state === 'done' ? 'is-done' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={cls}>
      <div className={item.thumb ? 'q-thumb' : 'q-thumb ph'}>
        {item.thumb ? <img src={item.thumb} alt="" /> : 'img'}
      </div>
      <div className="q-body">
        <div className="q-name">
          {item.fileName ?? 'Photo'}
          {item.kind === 'video' && item.durationSec !== undefined && (
            <span className="tag tag-neutral" style={{ fontSize: 10, padding: '1px 8px', marginLeft: 4 }}>
              {formatDuration(item.durationSec)}
            </span>
          )}
        </div>

        {item.state === 'uploading' && (
          <>
            <div className="q-bar"><i style={{ width: `${Math.round((item.progress ?? 0) * 100)}%` }} /></div>
            <div className="q-sub">{Math.round((item.progress ?? 0) * 100)}% · {estimateLabel(item)}</div>
          </>
        )}
        {/* "Queued" now covers resizing as well as waiting for a slot, and
            the small version is made here before anything is sent — so neither
            of these says what the old copy said. */}
        {item.state === 'queued' && <div className="q-sub">Getting it ready</div>}
        {item.state === 'processing' && <div className="q-sub">Nearly there</div>}
        {item.state === 'done' && <div className="q-sub">In the gallery</div>}
        {(item.state === 'failed' || item.state === 'rejected') && <div className="q-sub">{item.error}</div>}
      </div>

      {item.state === 'done' ? (
        <div className="q-tick" aria-label="Uploaded"><IconCheck size={18} /></div>
      ) : item.state === 'failed' ? (
        <button type="button" className="btn btn-primary" style={{ flex: 'none', minHeight: 40, paddingInline: 16 }} onClick={() => retry(item.id)}>
          Retry
        </button>
      ) : (
        <button
          type="button" className="btn btn-icon btn-secondary" style={{ flex: 'none', width: 40, height: 40 }}
          onClick={() => cancel(item.id)} aria-label={item.state === 'rejected' ? 'Dismiss' : 'Cancel this upload'}
        >
          <IconClose size={16} />
        </button>
      )}
    </div>
  );
}
